/**
 * Iter D.1 — Default measurement-category → estimate-service auto-link map.
 *
 * MVP fallback ONLY. Matching priority when auto-linking a saved
 * measurement to a line on an existing estimate:
 *
 *   1. Exact normalized service_category match on any line.
 *   2. `CATEGORY_TO_SERVICE_KEYS` lookup vs. the line's `service_key`.
 *   3. If still ambiguous or zero matches → return all lines so the UI
 *      can prompt the user to choose (or add a compatible service).
 *
 * The backend link endpoint is index-based (`line_item_index`) because
 * legacy line items don't carry a stable per-row id yet. This helper
 * therefore returns positional indices, which the workspace passes to
 * POST /api/measurements/{id}/links.
 */

export const CATEGORY_TO_SERVICE_KEYS = {
  roof:              ["roof_wash", "roof_soft_wash", "roof_cleaning"],
  house:             ["house_wash", "house_soft_wash", "siding_wash"],
  driveway:          ["concrete_cleaning", "driveway_cleaning"],
  deck:              ["deck_cleaning", "deck_wash"],
  fence:             ["fence_cleaning", "fence_wash"],
  gutter:            ["gutter_cleaning", "gutters"],
  landscape_area:    ["mulch"],
  landscape_volume:  ["rock", "gravel"],
  sidewalk:          ["sidewalk_cleaning", "concrete_cleaning"],
  patio:             ["patio_cleaning", "concrete_cleaning"],
  parking_lot:       ["parking_lot_cleaning", "parking_lot_striping", "parking_lot_seal"],
  // Iter D.0 — Solar measurement category. This maps to future solar service
  // keys; no pricing/production/ROI logic is implemented at this iteration.
  solar:             ["solar", "solar_install", "solar_array", "solar_panels"],
  other:             [], // requires custom label + explicit user pick
};

export const MEASUREMENT_CATEGORIES = [
  { key: "roof",             label: "Roof",              geom: "area" },
  { key: "house",            label: "House",             geom: "area" },
  { key: "driveway",         label: "Driveway",          geom: "area" },
  { key: "deck",             label: "Deck",              geom: "area" },
  { key: "patio",            label: "Patio",             geom: "area" },
  { key: "sidewalk",         label: "Sidewalk",          geom: "area" },
  { key: "parking_lot",      label: "Parking Lot",       geom: "area" },
  { key: "landscape_area",   label: "Landscape",         geom: "area" },
  { key: "landscape_volume", label: "Landscape volume",  geom: "area" },
  { key: "fence",            label: "Fence",             geom: "linear" },
  { key: "gutter",           label: "Gutter",            geom: "linear" },
  { key: "solar",            label: "Solar",             geom: "area" },
  { key: "other",            label: "Other (custom)",    geom: "any" },
];

/**
 * Resolve auto-link candidates.
 *
 * @param {object} arg
 * @param {object} arg.measurement            — must have `service_category`
 * @param {Array}  arg.estimateLineItems      — line items in their true array order
 * @returns {
 *   {line_item_index: number, matched_by: string} |
 *   {candidates: Array<{index:number, line:object}>, matched_by: string}
 * }
 */
export function resolveAutoLink({ measurement, estimateLineItems = [] }) {
  const cat = (measurement?.service_category || "").toLowerCase();
  const indexed = estimateLineItems.map((line, index) => ({ index, line }));

  if (!cat) return { candidates: indexed, matched_by: "no_category" };

  // 1. Exact service_category match on a line.
  const byCategory = indexed.filter(
    (x) => (x.line.service_category || "").toLowerCase() === cat
  );
  if (byCategory.length === 1) return { line_item_index: byCategory[0].index, matched_by: "category" };
  if (byCategory.length > 1)   return { candidates: byCategory, matched_by: "category" };

  // 2. Default map: match by service_key.
  const keys = CATEGORY_TO_SERVICE_KEYS[cat] || [];
  const byKey = indexed.filter((x) =>
    keys.includes((x.line.service_key || "").toLowerCase())
  );
  if (byKey.length === 1) return { line_item_index: byKey[0].index, matched_by: "default_map" };
  if (byKey.length > 1)   return { candidates: byKey, matched_by: "default_map" };

  // 3. Nothing matched — user must pick from all lines or add a compatible service.
  return { candidates: indexed, matched_by: "none" };
}

export function categoryLabel(key) {
  const m = MEASUREMENT_CATEGORIES.find((c) => c.key === key);
  return m ? m.label : key;
}

/**
 * Best-effort category inference from an estimate line. Prefers an explicit
 * `service_category` on the line; falls back to matching the line's `name`
 * against the reverse of `CATEGORY_TO_SERVICE_KEYS`. Returns `null` when
 * nothing sensible can be inferred — the caller should then skip any
 * category-driven UX and fall back to the generic workspace open.
 */
export function inferLineCategory(line) {
  if (!line) return null;
  if (line.service_category) return String(line.service_category).toLowerCase();
  const name = String(line.name || "").toLowerCase();
  if (!name) return null;
  // First: try service_key substring matches.
  for (const [cat, keys] of Object.entries(CATEGORY_TO_SERVICE_KEYS)) {
    for (const k of keys) {
      const readable = k.replace(/_/g, " ");
      if (name.includes(readable)) return cat;
    }
  }
  // Then: try raw category-name substring (e.g. "Roof soft wash" → roof).
  for (const c of MEASUREMENT_CATEGORIES) {
    if (name.includes(c.key)) return c.key;
  }
  return null;
}
